import { createContext, useContext, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import type { Entry } from 'contentful';

import type { ILandingPageFields } from '../../../../declarations/contentful';
import { generatePresetPictureSetProps } from '../../../shared/performance/pictures';
import type { PictureSourceSpec } from '../../../shared/performance/pictures';
import { HERO_IMAGE_DESKTOP_PROPS, HERO_IMAGE_MOBILE_PROPS } from './heroConstants';
import type { HeroContextValue, HeroProviderProps } from './heroTypes';

const HeroContext = createContext<HeroContextValue | null>(null);

export const HeroProvider = ({ variant, analyticsPrefix, children }: HeroProviderProps) => {
  return (
    <HeroContext.Provider value={{ variant, analyticsPrefix }}>{children}</HeroContext.Provider>
  );
};

export const useHeroContext = (): HeroContextValue => {
  const context = useContext(HeroContext);
  if (!context) {
    throw new Error('useHeroContext must be used within a HeroProvider');
  }
  return context;
};

export const useStickyCTA = () => {
  const [hasBeenInView, setHasBeenInView] = useState(false);

  const { ref: addressEntryRef, inView: addressEntryInView } = useInView();

  useEffect(() => {
    if (addressEntryInView) {
      setHasBeenInView(true);
    }
  }, [addressEntryInView]);

  // Only show after user has scrolled past it at least once
  const showStickyCTAUtility = hasBeenInView && !addressEntryInView;

  return {
    addressEntryRef,
    showStickyCTAUtility,
  };
};

interface PreloadHeroImagesParams {
  imageDesktopUrl?: string;
  imageMobileUrl?: string;
}

export const preloadHeroImages = ({
  imageDesktopUrl,
  imageMobileUrl,
}: PreloadHeroImagesParams): PictureSourceSpec => {
  const preloadImages: PictureSourceSpec = [];

  if (imageDesktopUrl) {
    const { sources } = generatePresetPictureSetProps({
      ...HERO_IMAGE_DESKTOP_PROPS,
      photoUrl: imageDesktopUrl,
    });
    preloadImages.push(...sources);
  }

  if (imageMobileUrl) {
    const { sources } = generatePresetPictureSetProps({
      ...HERO_IMAGE_MOBILE_PROPS,
      photoUrl: imageMobileUrl,
    });
    preloadImages.push(...sources);
  }

  return preloadImages;
};

export const getStickyCTAFromRoot = (root?: Entry<ILandingPageFields>) => {
  return root?.fields?.stickyCta || '';
};
